// The Sandboxes pages read Sandboxes as the API serves them (api/nodus/v1/sandbox_types.go). These are the fields the
// pages show; everything else, including env, stays in View YAML.
import type { Condition, Cost, KObject } from "@nodus/api";
import type { SandboxSpec as ApiSandboxSpec } from "@nodus/api/gen/nodus_dev_v1";

export type Scope = { org: string; project: string };

export type SandboxSpec = Partial<ApiSandboxSpec>;

export type SandboxStatus = {
  phase?: string;
  conditions?: Condition[];
  reason?: string;
  message?: string;
  stopReason?: string;
  activity?: "Busy" | "Idle";
  lastActivityTime?: string;
  expirationTime?: string;
  attempt?: { name: string; epoch: number };
  snapshot?: { time: string; reason: string };
  cost?: Cost;
};

export type Sandbox = KObject<SandboxSpec, SandboxStatus>;

/** The values the server applies to a Sandbox that leaves a field unset, and the image the clients fill in. */
export const SANDBOX_DEFAULTS = {
  image: "nodus/agent-tools",
  cpu: "1",
  memory: "1Gi",
  disk: "10Gi",
  egress: "Deny",
  idleTimeout: "5m",
  onIdle: "Stop",
  maxLifetime: "24h",
  workingDir: "/workspace",
  continuity: "Snapshotted",
} as const;

/** The default CPU Sandbox working and saved directory. */
export const WORKSPACE_DIR = "/workspace";

export function isStopped(sb: Sandbox): boolean {
  const phase = sb.status?.phase;
  return sb.spec?.state === "Stopped" || phase === "Stopped" || phase === "Stopping";
}

export function isRunning(sb: Sandbox): boolean {
  return sb.status?.phase === "Running";
}

export function isEphemeral(sb: Sandbox): boolean {
  return (sb.spec?.continuity?.mode ?? (sb.spec?.resources?.gpu ? "Ephemeral" : SANDBOX_DEFAULTS.continuity)) === "Ephemeral";
}

/** The declared saved directory, or the existing CPU default. */
export function stateDirectory(sb: Sandbox): string {
  return sb.spec?.continuity?.stateDir ?? "/nodus/state";
}

/** "1 vCPU · 1 GiB": CPU and memory as the quantities the spec carries, with defaults for what it leaves out. */
export function resourcesLabel(spec: SandboxSpec | undefined): string {
  const cpu = spec?.resources?.cpu ?? SANDBOX_DEFAULTS.cpu;
  const vcpu = cpu.endsWith("m") ? String(Number(cpu.slice(0, -1)) / 1000) : cpu;
  return `${vcpu} vCPU · ${bytesLabel(spec?.resources?.memory ?? SANDBOX_DEFAULTS.memory)}`;
}

/** "1Gi" as "1 GiB"; a quantity without a binary suffix stays as written. */
export function bytesLabel(quantity: string): string {
  const m = /^(\d+(?:\.\d+)?)(Ki|Mi|Gi|Ti)$/.exec(quantity);
  return m ? `${m[1]} ${m[2]}B` : quantity;
}

/** The image reference without its digest. */
export function imageLabel(spec: SandboxSpec | undefined): string {
  return (spec?.image ?? SANDBOX_DEFAULTS.image).replace(/@sha256:[0-9a-f]+$/, "");
}

const STOP_REASONS: Record<string, string> = {
  User: "Stopped by you",
  Idle: "Stopped after the idle timeout",
  InsufficientCredits: "Out of credits",
  BudgetExceeded: "A budget limit was reached",
  MaxCostReached: "The cost limit was reached",
  MaxLifetime: "The maximum lifetime passed",
  NodeLost: "The node was lost",
  Failed: "The sandbox failed",
};

/** Why a stopped Sandbox is stopped, in words; the raw reason when the API sends one this build does not know. */
export function stopReasonLabel(reason: string | undefined): string | undefined {
  return reason ? (STOP_REASONS[reason] ?? reason) : undefined;
}

/** `sandbox/<name>`, the reference the CLI takes. */
export function ref(name: string): string {
  return `sandbox/${name}`;
}
